import type { ComponentType } from "react";
import { useMemo } from "react";
import type { DashboardTab } from "@app/config/tabs";
import { ErrorBoundary } from "@app/components/shared/ErrorBoundary";
import { OverviewTab } from "@app/features/overview/OverviewTab";
import { ConnectionsTab } from "@app/features/connections/ConnectionsTab";
import { IntegrationsTab } from "@app/features/integrations/IntegrationsTab";
import { MarketDataTab } from "@app/features/market-data/MarketDataTab";
import { AiAssistantTab } from "@app/features/ai-assistant/AiAssistantTab";
import { TradingSystemTab } from "@app/features/trading-system/TradingSystemTab";
import { SystemTab } from "@app/features/system/SystemTab";
import { AiSupervisorTab } from "@app/features/ai-supervisor/AiSupervisorTab";
import { TradingLogicControlsTab } from "@app/features/trading-logic-controls/TradingLogicControlsTab";
import { UsersTab } from "@app/features/users/UsersTab";
import { PlansTab } from "@app/features/plans/PlansTab";
import { SubscriptionsTab } from "@app/features/subscriptions/SubscriptionsTab";
import { UserTradingSystemTab } from "@app/features/user-trading-system/UserTradingSystemTab";
import { AdminTradingPipelineTab } from "@app/features/admin-trading-pipeline/AdminTradingPipelineTab";
import { SupportTab } from "@app/features/support/SupportTab";
import { AdminSupportTab } from "@app/features/admin-support/AdminSupportTab";
import { AdminOperationsTab } from "@app/features/admin-operations/AdminOperationsTab";
import { AdminCoinsWalletsTab } from "@app/features/admin-coins-wallets/AdminCoinsWalletsTab";
import { AdminPaymentMonitoringTab } from "@app/features/admin-payment-monitoring/AdminPaymentMonitoringTab";
import { AdminDLQTab } from "@app/features/admin-dlq/AdminDLQTab";
import { AdminExitSignalsTab } from "@app/features/admin-exit-signals/AdminExitSignalsTab";
import { AdminHealthOpsTab } from "@app/features/admin-health-ops/AdminHealthOpsTab";
import { AdminSystemStorageTab } from "@app/features/admin-system-storage/AdminSystemStorageTab";
import { AdminMetricsTab } from "@app/features/admin-metrics/AdminMetricsTab";
import { AdminControlsTab } from "@app/features/admin-controls/AdminControlsTab";
import { AdminDiagnosticsTab } from "@app/features/admin-diagnostics/AdminDiagnosticsTab";
import { AdminReconciliationTab } from "@app/features/admin-reconciliation/AdminReconciliationTab";
import { AdminMembershipsTab } from "@app/features/admin-memberships/AdminMembershipsTab";
import { AdminTradeExecutionTab } from "@app/features/admin-trade-execution/AdminTradeExecutionTab";
import { AiEngineMonitorTab } from "@app/features/ai-engine-monitor/AiEngineMonitorTab";
import { AdminTradeHistoryTab } from "@app/features/admin-trade-history/AdminTradeHistoryTab";
import { XGBoostControlCenterTab } from "@app/features/xgboost-control-center/XGBoostControlCenterTab";
import { LightGBMControlCenterTab } from "@app/features/lightgbm-control-center/LightGBMControlCenterTab";
import TradeFlowTrackingTab from "@app/features/trade-flow-tracking/TradeFlowTrackingTab";

type TabPanelsProps = {
  tabs: DashboardTab[];
  activeTab: string;
};

type TabComponentRegistry = Partial<Record<string, ComponentType>>;

const tabComponents: TabComponentRegistry = {
  overview: OverviewTab,
  connections: ConnectionsTab,
  integrations: IntegrationsTab,
  "market-data": MarketDataTab,
  "ai-assistant": AiAssistantTab,
  "ai-supervisor": AiSupervisorTab,
  "trading-system": TradingSystemTab,
  "user-trading-system": UserTradingSystemTab,
  "trading-logic-controls": TradingLogicControlsTab,
  "admin-trading-pipeline": AdminTradingPipelineTab,
  system: SystemTab,
  users: UsersTab,
  plans: PlansTab,
  subscriptions: SubscriptionsTab,
  support: SupportTab,
  "admin-support": AdminSupportTab,
  "admin-operations": AdminOperationsTab,
  "admin-coins-wallets": AdminCoinsWalletsTab,
  "admin-payment-monitoring": AdminPaymentMonitoringTab,
  "admin-dlq": AdminDLQTab,
  "admin-exit-signals": AdminExitSignalsTab,
  "admin-health-ops": AdminHealthOpsTab,
  "admin-system-storage": AdminSystemStorageTab,
  "admin-metrics": AdminMetricsTab,
  "admin-controls": AdminControlsTab,
  "admin-diagnostics": AdminDiagnosticsTab,
  "admin-reconciliation": AdminReconciliationTab,
  "admin-positions-monitor": AdminMembershipsTab,
  "admin-trade-execution": AdminTradeExecutionTab,
  "ai-engine-monitor": AiEngineMonitorTab,
  "admin-trade-history": AdminTradeHistoryTab,
  "xgboost-control-center": XGBoostControlCenterTab,
  "lgbm-control-center": LightGBMControlCenterTab,
  "trade-flow-tracking": TradeFlowTrackingTab,
};

const placeholderContent: Record<string, string> = {
  configuration:
    "Adjust portfolio parameters, allowed symbols, and operational limits.",
  "market-data": "Live charts, price tickers, and open-position analytics.",
  "trading-system":
    "Track each stage of the AI workflow from data collection to execution.",
  "ai-assistant": "Conversational tools, memory controls, and knowledge uploads.",
  "ai-supervisor": "Real-time supervision of AI automation, guardrails, and performance insights.",
  connections:
    "Infrastructure admin: news collectors, AI backends, and internal services.",
  integrations:
    "Link personal trading accounts, messaging apps, and webhook automations.",
  system: "Global system options, risk guardrails, and monitoring logs.",
};

function TabPanels({ tabs, activeTab }: TabPanelsProps) {
  const selectedTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTab) ?? tabs[0],
    [tabs, activeTab],
  );

  const Component = tabComponents[selectedTab.id];

  return (
    <div className="tab-panel" role="tabpanel">
      {Component ? (
        <ErrorBoundary>
        <Component />
        </ErrorBoundary>
      ) : (
        <section className="panel">
          <h2>{selectedTab.title}</h2>
          <p>{placeholderContent[selectedTab.id] ?? "Content coming soon."}</p>
        </section>
      )}
    </div>
  );
}

export default TabPanels;

